import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Plus,
  Server,
  Database,
  Cloud,
  GitBranch,
  Trash2,
  PauseCircle,
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';

import { useDiagramStore } from '../store/diagramStore';
import {
  ServiceType,
  ServiceEndpoint,
  ConvoyRoute,
} from '../types/diagram.types';

const SidebarContainer = styled(motion.div)`
  position: absolute;
  right: 0;
  top: 0;
  height: 100vh;
  width: 400px;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
`;

const SidebarContent = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Button = styled(motion.button)<{
  $variant?: 'primary' | 'secondary' | 'danger';
}>`
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  ${(props) => {
    switch (props.$variant) {
      case 'danger':
        return `
          background: #f44336;
          color: white;
          &:hover { background: #d32f2f; }
        `;
      case 'secondary':
        return `
          background: #f5f5f5;
          color: #333;
          &:hover { background: #e0e0e0; }
        `;
      default:
        return `
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          &:hover { opacity: 0.9; }
        `;
    }
  }}
`;

const ServiceTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: 16px;
`;

const ServiceTypeButton = styled(motion.button)<{ $selected: boolean }>`
  padding: 12px;
  border: 2px solid ${(props) => (props.$selected ? '#667eea' : '#e0e0e0')};
  border-radius: 8px;
  background: ${(props) =>
    props.$selected ? 'rgba(102, 126, 234, 0.1)' : 'white'};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #667eea;
    background: rgba(102, 126, 234, 0.05);
  }
`;

const EndpointItem = styled.div`
  padding: 12px;
  background: #f9f9f9;
  border-radius: 6px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: start;
`;

const EndpointInfo = styled.div`
  flex: 1;
`;

const EndpointActions = styled.div`
  display: flex;
  gap: 4px;
`;

const IconButton = styled(motion.button)`
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  cursor: pointer;
`;

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const {
    selectedNodeId,
    currentDiagram,
    addNode,
    updateNode,
    deleteNode,
    isEditMode,
  } = useDiagramStore();

  const selectedNode = currentDiagram?.nodes.find(
    (n) => n.id === selectedNodeId,
  );

  const [formData, setFormData] = useState({
    name: '',
    type: 'internal' as ServiceType,
    description: '',
    endpoints: [] as ServiceEndpoint[],
    convoyRoutes: [] as ConvoyRoute[],
  });

  const [newEndpoint, setNewEndpoint] = useState({
    path: '',
    method: 'GET' as ServiceEndpoint['method'],
    description: '',
    isPaused: false,
  });

  const [newConvoyRoute, setNewConvoyRoute] = useState({
    name: '',
    description: '',
    isPaused: false,
  });

  const [showEndpointForm, setShowEndpointForm] = useState(false);
  const [showConvoyForm, setShowConvoyForm] = useState(false);

  useEffect(() => {
    if (selectedNode) {
      setFormData({
        name: selectedNode.name,
        type: selectedNode.type,
        description: selectedNode.description || '',
        endpoints: selectedNode.endpoints || [],
        convoyRoutes: selectedNode.convoyRoutes || [],
      });
    } else {
      setFormData({
        name: '',
        type: 'internal',
        description: '',
        endpoints: [],
        convoyRoutes: [],
      });
    }
  }, [selectedNode]);

  const serviceTypes: {
    type: ServiceType;
    icon: React.ReactNode;
    label: string;
  }[] = [
    { type: 'main', icon: <Server size={20} />, label: 'Main Service' },
    { type: 'internal', icon: <Database size={20} />, label: 'Internal' },
    { type: 'external', icon: <Cloud size={20} />, label: 'External' },
    { type: 'convoy', icon: <GitBranch size={20} />, label: 'Convoy' },
  ];

  const handleAddEndpoint = () => {
    if (!newEndpoint.path) {
      toast.error('Endpoint path is required');
      return;
    }

    const endpoint: ServiceEndpoint = {
      id: uuidv4(),
      ...newEndpoint,
    };

    setFormData((prev) => ({
      ...prev,
      endpoints: [...prev.endpoints, endpoint],
    }));

    setNewEndpoint({
      path: '',
      method: 'GET',
      description: '',
      isPaused: false,
    });
    setShowEndpointForm(false);
    toast.success('Endpoint added');
  };

  const handleAddConvoyRoute = () => {
    if (!newConvoyRoute.name) {
      toast.error('Route name is required');
      return;
    }

    const route: ConvoyRoute = {
      id: uuidv4(),
      ...newConvoyRoute,
    };

    setFormData((prev) => ({
      ...prev,
      convoyRoutes: [...prev.convoyRoutes, route],
    }));

    setNewConvoyRoute({
      name: '',
      description: '',
      isPaused: false,
    });
    setShowConvoyForm(false);
    toast.success('Convoy route added');
  };

  const handleSave = () => {
    if (!formData.name) {
      toast.error('Service name is required');
      return;
    }

    if (selectedNode) {
      updateNode(selectedNode.id, formData);
      toast.success('Service updated');
    } else {
      // Generate random position for new node
      const position = {
        x: Math.random() * 500 + 100,
        y: Math.random() * 400 + 100,
      };

      addNode({
        ...formData,
        position,
      });
      toast.success('Service added to diagram');
    }

    onClose();
  };

  const handleDelete = () => {
    if (selectedNode) {
      deleteNode(selectedNode.id);
      toast.success('Service deleted');
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <SidebarContainer
          initial={{ x: 400 }}
          animate={{ x: 0 }}
          exit={{ x: 400 }}
          transition={{ type: 'spring', damping: 20 }}
        >
          <SidebarHeader>
            <h2 style={{ margin: 0, fontSize: 20 }}>
              {selectedNode ? 'Edit Service' : 'Add Service'}
            </h2>
            <IconButton onClick={onClose}>
              <X size={24} color="white" />
            </IconButton>
          </SidebarHeader>

          <SidebarContent>
            <Section>
              <SectionTitle>Service Type</SectionTitle>
              <ServiceTypeGrid>
                {serviceTypes.map(({ type, icon, label }) => (
                  <ServiceTypeButton
                    key={type}
                    $selected={formData.type === type}
                    onClick={() => setFormData((prev) => ({ ...prev, type }))}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {icon}
                    <span style={{ fontSize: 12 }}>{label}</span>
                  </ServiceTypeButton>
                ))}
              </ServiceTypeGrid>
            </Section>

            <Section>
              <FormGroup>
                <Label>Service Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g., FreshApp, Payment Service"
                />
              </FormGroup>

              <FormGroup>
                <Label>Description</Label>
                <TextArea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Describe the service's purpose and functionality..."
                />
              </FormGroup>
            </Section>

            <Section>
              <SectionTitle>
                Endpoints
                <Button
                  $variant="secondary"
                  onClick={() => setShowEndpointForm(!showEndpointForm)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Plus size={16} />
                  Add
                </Button>
              </SectionTitle>

              {showEndpointForm && (
                <div
                  style={{
                    padding: 12,
                    background: '#f5f5f5',
                    borderRadius: 8,
                    marginBottom: 12,
                  }}
                >
                  <FormGroup>
                    <Label>Path *</Label>
                    <Input
                      value={newEndpoint.path}
                      onChange={(e) =>
                        setNewEndpoint((prev) => ({
                          ...prev,
                          path: e.target.value,
                        }))
                      }
                      placeholder="/api/endpoint"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Method</Label>
                    <Select
                      value={newEndpoint.method}
                      onChange={(e) =>
                        setNewEndpoint((prev) => ({
                          ...prev,
                          method: e.target.value as ServiceEndpoint['method'],
                        }))
                      }
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="DELETE">DELETE</option>
                      <option value="PATCH">PATCH</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <CheckboxLabel>
                      <input
                        type="checkbox"
                        checked={newEndpoint.isPaused}
                        onChange={(e) =>
                          setNewEndpoint((prev) => ({
                            ...prev,
                            isPaused: e.target.checked,
                          }))
                        }
                      />
                      <PauseCircle size={16} />
                      Paused
                    </CheckboxLabel>
                  </FormGroup>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button onClick={handleAddEndpoint}>Add</Button>
                    <Button
                      $variant="secondary"
                      onClick={() => setShowEndpointForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {formData.endpoints.map((endpoint) => (
                <EndpointItem key={endpoint.id}>
                  <EndpointInfo>
                    <div style={{ fontWeight: 500, marginBottom: 4 }}>
                      {endpoint.method && (
                        <span style={{ color: '#667eea' }}>
                          {endpoint.method}
                        </span>
                      )}{' '}
                      {endpoint.path}
                    </div>
                    {endpoint.isPaused && (
                      <div style={{ fontSize: 12, color: '#f44336' }}>
                        <PauseCircle size={12} /> Paused
                      </div>
                    )}
                  </EndpointInfo>
                  <EndpointActions>
                    <IconButton
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          endpoints: prev.endpoints.filter(
                            (e) => e.id !== endpoint.id,
                          ),
                        }))
                      }
                    >
                      <Trash2 size={16} color="#f44336" />
                    </IconButton>
                  </EndpointActions>
                </EndpointItem>
              ))}
            </Section>

            {formData.type === 'convoy' && (
              <Section>
                <SectionTitle>
                  Convoy Routes
                  <Button
                    $variant="secondary"
                    onClick={() => setShowConvoyForm(!showConvoyForm)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Plus size={16} />
                    Add
                  </Button>
                </SectionTitle>

                {showConvoyForm && (
                  <div
                    style={{
                      padding: 12,
                      background: '#f5f5f5',
                      borderRadius: 8,
                      marginBottom: 12,
                    }}
                  >
                    <FormGroup>
                      <Label>Route Name *</Label>
                      <Input
                        value={newConvoyRoute.name}
                        onChange={(e) =>
                          setNewConvoyRoute((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="e.g., CONVOY_ERP_ORDER_ENDPOINT_ID"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Description</Label>
                      <Input
                        value={newConvoyRoute.description}
                        onChange={(e) =>
                          setNewConvoyRoute((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        placeholder="Route description..."
                      />
                    </FormGroup>
                    <FormGroup>
                      <CheckboxLabel>
                        <input
                          type="checkbox"
                          checked={newConvoyRoute.isPaused}
                          onChange={(e) =>
                            setNewConvoyRoute((prev) => ({
                              ...prev,
                              isPaused: e.target.checked,
                            }))
                          }
                        />
                        <PauseCircle size={16} />
                        Paused
                      </CheckboxLabel>
                    </FormGroup>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button onClick={handleAddConvoyRoute}>Add</Button>
                      <Button
                        $variant="secondary"
                        onClick={() => setShowConvoyForm(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {formData.convoyRoutes.map((route) => (
                  <EndpointItem key={route.id}>
                    <EndpointInfo>
                      <div style={{ fontWeight: 500, marginBottom: 4 }}>
                        {route.name}
                      </div>
                      {route.description && (
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {route.description}
                        </div>
                      )}
                      {route.isPaused && (
                        <div style={{ fontSize: 12, color: '#f44336' }}>
                          <PauseCircle size={12} /> Paused
                        </div>
                      )}
                    </EndpointInfo>
                    <EndpointActions>
                      <IconButton
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            convoyRoutes: prev.convoyRoutes.filter(
                              (r) => r.id !== route.id,
                            ),
                          }))
                        }
                      >
                        <Trash2 size={16} color="#f44336" />
                      </IconButton>
                    </EndpointActions>
                  </EndpointItem>
                ))}
              </Section>
            )}

            <Section>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  onClick={handleSave}
                  disabled={!isEditMode}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ flex: 1 }}
                >
                  {selectedNode ? 'Update Service' : 'Add Service'}
                </Button>
                {selectedNode && (
                  <Button
                    $variant="danger"
                    onClick={handleDelete}
                    disabled={!isEditMode}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Trash2 size={16} />
                    Delete
                  </Button>
                )}
              </div>
              {!isEditMode && (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: '#666',
                    textAlign: 'center',
                  }}
                >
                  Enable edit mode to make changes
                </div>
              )}
            </Section>
          </SidebarContent>
        </SidebarContainer>
      )}
    </AnimatePresence>
  );
};
